import { Formik } from "formik";
import { Loader } from "lucide-react";
import { Suspense, useCallback, useMemo } from "react";
import { Await, useLoaderData, useNavigate, useParams } from "react-router-dom";
import { userSchema } from "../../Admin/Users/schema/user";
import userApi from "@/api/user";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { CreditTransactionForm } from "@/pages/Admin/Users/CreditTransactionForm";
import { CreditTransactionsList } from "@/pages/Admin/Users/CreditTransactionsList";
import { AdminDetailsPage } from "@/pages/Portal/components/AdminDetailsPage";
import { AdminReadOnlyField } from "@/pages/Portal/components/AdminReadOnlyField";
import { addNotification } from "@/store/app/slice";
import { getIs1TemplateTenant } from "@/store/tenant/selectors";
import { NotificationMessageType, NotificationType, User } from "@/types";
import { ICreditAction } from "@/types/creditAction";
import { getIsProductionEnv } from "@/utils/env";

const userRoleItems = [
    { id: "user", name: "User" },
    { id: "admin", name: "Admin" },
];

export const PortalUserDetailsPage = () => {
    const { getPortalUser } = useLoaderData() as {
        getPortalUser: Promise<{
            user: User;
            creditActions: Array<ICreditAction>;
        }>;
    };

    return (
        <Suspense fallback={<Loader />}>
            <Await
                resolve={getPortalUser}
                errorElement={<div>Failed to load user</div>}
            >
                {resolvedData => (
                    <PortalUserFormFields
                        user={resolvedData.user}
                        creditActions={resolvedData.creditActions}
                    />
                )}
            </Await>
        </Suspense>
    );
};

const PortalUserFormFields = ({
    user,
    creditActions,
}: {
    user?: User;
    creditActions: Array<ICreditAction>;
}) => {
    const isProduction = getIsProductionEnv();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { userId: id } = useParams();
    const userId = id !== "new" ? id : undefined;
    const is1TemplateTenant = useAppSelector(getIs1TemplateTenant);

    const initialValues: Partial<User> = useMemo(
        () => ({
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            email: user?.email || "",
            role: user?.role || "user",
            isEmailVerified: user?.isEmailVerified || false,
            loginMethod: user?.loginMethod || undefined,
            stripeCustomerId: user?.stripeCustomerId || "",
            credits: user?.credits || { balance: 0, lastUpdated: null },
            flags: user?.flags || {},
            marketingOptIns: user?.marketingOptIns || [],
            preferences: user?.preferences || {
                experienceLevel: "",
                industries: [],
                jobTypes: [],
                jobRoles: [],
            },
            acceptedTerms: user?.acceptedTerms || false,
        }),
        [user],
    );

    const handleError = useCallback(
        (err: unknown, title: string, messageType: NotificationMessageType) => {
            let errorMessage;
            if (typeof err === "string") {
                errorMessage = err;
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }

            dispatch(
                addNotification({
                    title,
                    desc: errorMessage,
                    messageType,
                    type: NotificationType.ERROR,
                }),
            );
        },
        [dispatch],
    );

    const handleSubmit = async (values: Partial<User>) => {
        try {
            // Clean up the values before sending to backend
            const cleanedValues = { ...values };

            // Remove all read-only fields that shouldn't be sent for updates
            delete cleanedValues.createdAt;
            delete cleanedValues.scheduledDeletionDate;
            delete cleanedValues.lastLoginAt;
            delete cleanedValues.id; // Also remove id as it's typically read-only

            await userApi.saveUserApi(cleanedValues, userId);
            if (!userId) {
                navigate("/portal/users-new", { replace: true });
            }

            dispatch(
                addNotification({
                    title: "Successfully saved!",
                    desc: "Your details are successfully saved!",
                    messageType: NotificationMessageType.ADMIN_USER_SAVE,
                    type: NotificationType.SUCCESS,
                }),
            );
        } catch (err: unknown) {
            handleError(
                err,
                "Failed to save",
                NotificationMessageType.ADMIN_USER_SAVE,
            );
        }
    };

    const handleDelete = useCallback(async () => {
        if (isProduction) {
            dispatch(
                addNotification({
                    title: "Unable to complete action",
                    desc: "You cannot delete users in production via the admin panel.",
                    messageType: NotificationMessageType.ADMIN_USER_DELETE,
                    type: NotificationType.WARNING,
                }),
            );
            return;
        }

        if (!userId) {
            navigate("/portal/users-new", { replace: true });
            return;
        }

        try {
            await userApi.deleteUserApi(userId);
            navigate("/portal/users-new", { replace: true });
            dispatch(
                addNotification({
                    title: "Successfully deleted",
                    desc: "User successfully deleted",
                    messageType: NotificationMessageType.ADMIN_USER_DELETE,
                    type: NotificationType.SUCCESS,
                }),
            );
        } catch (e) {
            handleError(
                e,
                "Failed to delete",
                NotificationMessageType.ADMIN_USER_DELETE,
            );
        }
    }, [isProduction, dispatch, userId, navigate, handleError]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={userSchema}
            onSubmit={handleSubmit}
        >
            {({
                values,
                errors,
                handleChange,
                setFieldValue,
                submitForm,
                validateForm,
            }) => {
                const handleSaveWithValidation = async () => {
                    const validationErrors = await validateForm();
                    if (Object.keys(validationErrors).length > 0) {
                        dispatch(
                            addNotification({
                                title: "Validation Error",
                                desc: "Please fix the validation errors before saving.",
                                messageType:
                                    NotificationMessageType.VALIDATION_ERROR,
                                type: NotificationType.ERROR,
                            }),
                        );
                        console.error(validationErrors);
                        return;
                    }
                    submitForm();
                };

                return (
                    <AdminDetailsPage
                        pageTitle="User"
                        listHref="/portal/users-new"
                        onSave={handleSaveWithValidation}
                        onDelete={handleDelete}
                        showDelete={!!userId}
                    >
                        <Tabs
                            defaultValue="details"
                            className="w-full"
                        >
                            <TabsList className="grid w-full grid-cols-3">
                                <TabsTrigger value="details">
                                    Details
                                </TabsTrigger>
                                {is1TemplateTenant && (
                                    <>
                                        <TabsTrigger value="credits">
                                            Credits
                                        </TabsTrigger>
                                        <TabsTrigger value="transactions">
                                            Transactions
                                        </TabsTrigger>
                                    </>
                                )}
                            </TabsList>

                            <TabsContent
                                value="details"
                                className="space-y-2"
                            >
                                {/* Basic Information */}
                                <Card>
                                    <CardHeader className="px-6 pt-4">
                                        <CardTitle className="text-md">
                                            Basic Information
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-2">
                                            <AdminReadOnlyField
                                                label="ID"
                                                value={user?.id}
                                            />
                                            <div className="space-y-1">
                                                <Label
                                                    htmlFor="firstName"
                                                    className="text-sm font-medium"
                                                >
                                                    First Name *
                                                </Label>
                                                <Input
                                                    type="text"
                                                    name="firstName"
                                                    id="firstName"
                                                    value={values.firstName}
                                                    onChange={handleChange}
                                                    className="mt-1"
                                                />
                                                {errors.firstName && (
                                                    <p className="mt-1 text-sm text-admin-destructive">
                                                        {errors.firstName}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="space-y-1">
                                                <Label
                                                    htmlFor="lastName"
                                                    className="text-sm font-medium"
                                                >
                                                    Last Name *
                                                </Label>
                                                <Input
                                                    type="text"
                                                    name="lastName"
                                                    id="lastName"
                                                    value={values.lastName}
                                                    onChange={handleChange}
                                                    className="mt-1"
                                                />
                                                {errors.lastName && (
                                                    <p className="mt-1 text-sm text-admin-destructive">
                                                        {errors.lastName}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Account Settings */}
                                <Card>
                                    <CardHeader className="px-6 pt-4">
                                        <CardTitle className="text-md">
                                            Account Settings
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-2">
                                            <div className="space-y-1">
                                                <Label
                                                    htmlFor="email"
                                                    className="text-sm font-medium"
                                                >
                                                    Email *
                                                </Label>
                                                <Input
                                                    type="text"
                                                    name="email"
                                                    id="email"
                                                    value={values.email}
                                                    onChange={handleChange}
                                                    className="mt-1"
                                                />
                                                {errors.email && (
                                                    <p className="mt-1 text-sm text-admin-destructive">
                                                        {errors.email}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="space-y-1">
                                                <Label
                                                    htmlFor="role"
                                                    className="text-sm font-medium"
                                                >
                                                    Role *
                                                </Label>
                                                <p className="mb-2 text-xs text-admin-card-foreground">
                                                    &quot;user&quot; or
                                                    &quot;admin&quot;
                                                </p>
                                                <Select
                                                    value={values.role}
                                                    onValueChange={value =>
                                                        setFieldValue(
                                                            "role",
                                                            value,
                                                        )
                                                    }
                                                >
                                                    <SelectTrigger className="mt-1">
                                                        <SelectValue placeholder="Select a role" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {userRoleItems.map(
                                                            item => (
                                                                <SelectItem
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    value={
                                                                        item.id
                                                                    }
                                                                >
                                                                    {item.name}
                                                                </SelectItem>
                                                            ),
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                                {errors.role && (
                                                    <p className="mt-1 text-sm text-admin-destructive">
                                                        {errors.role}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center space-y-1">
                                                <div className="flex items-center space-x-2">
                                                    <Checkbox
                                                        id="isEmailVerified"
                                                        checked={
                                                            !!values.isEmailVerified
                                                        }
                                                        onCheckedChange={checked =>
                                                            setFieldValue(
                                                                "isEmailVerified",
                                                                checked,
                                                            )
                                                        }
                                                    />
                                                    <Label
                                                        htmlFor="isEmailVerified"
                                                        className="text-sm font-medium"
                                                    >
                                                        Is Email Verified?
                                                    </Label>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* System Information */}
                                {is1TemplateTenant && (
                                    <Card>
                                        <CardHeader className="px-6 pt-4">
                                            <CardTitle className="text-md">
                                                System Information
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                <AdminReadOnlyField
                                                    label="Login Method"
                                                    value={values.loginMethod}
                                                    description="The method used to login to the account"
                                                />
                                                <AdminReadOnlyField
                                                    label="Stripe Customer ID"
                                                    value={
                                                        values.stripeCustomerId
                                                    }
                                                    description="The Stripe customer ID associated with the user"
                                                />
                                                <AdminReadOnlyField
                                                    label="Created At"
                                                    value={formatDate(
                                                        values.createdAt,
                                                    )}
                                                    description="The date and time the user was created"
                                                />
                                                <AdminReadOnlyField
                                                    label="Last Login At"
                                                    value={formatDate(
                                                        values.lastLoginAt,
                                                    )}
                                                    description="The date and time the user last logged in"
                                                />
                                                <AdminReadOnlyField
                                                    label="Credits"
                                                    value={
                                                        values.credits?.balance
                                                    }
                                                    description="The number of credits the user has"
                                                />
                                                <AdminReadOnlyField
                                                    label="Scheduled Deletion Date"
                                                    value={formatDate(
                                                        values.scheduledDeletionDate,
                                                    )}
                                                    description="The date and time the user's account will be deleted"
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Additional Data */}
                                {is1TemplateTenant && (
                                    <Card>
                                        <CardHeader className="px-6 pt-4">
                                            <CardTitle className="text-md">
                                                Additional Data
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div>
                                                <AdminReadOnlyField
                                                    label="Flags"
                                                    value={JSON.stringify(
                                                        values.flags,
                                                        null,
                                                        2,
                                                    )}
                                                    description="Flags are used to track user behavior"
                                                />
                                            </div>
                                            <div>
                                                <AdminReadOnlyField
                                                    label="Marketing Opt Ins"
                                                    value={JSON.stringify(
                                                        values.marketingOptIns,
                                                        null,
                                                        2,
                                                    )}
                                                    description="Opt ins for marketing purposes"
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </TabsContent>

                            <TabsContent
                                value="credits"
                                className="space-y-4 bg-white px-2"
                            >
                                <CreditTransactionForm
                                    userId={userId!}
                                    userEmail={user?.email}
                                    creditActions={creditActions}
                                />
                            </TabsContent>

                            <TabsContent
                                value="transactions"
                                className="bg-white"
                            >
                                <CreditTransactionsList userId={userId!} />
                            </TabsContent>
                        </Tabs>
                    </AdminDetailsPage>
                );
            }}
        </Formik>
    );
};

const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return "";
    const date =
        typeof dateString === "string" ? new Date(dateString) : dateString;
    return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};
